import React from 'react';
import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle, RefreshCw } from "lucide-react";
import { useTranslation } from 'react-i18next';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error?: Error; resetError: () => void }>;
}

class ErrorBoundaryClass extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    });

    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return <FallbackComponent error={this.state.error} resetError={this.resetError} />;
      }

      return <DefaultErrorFallback error={this.state.error} resetError={this.resetError} />;
    }

    return this.props.children;
  }
}

const DefaultErrorFallback: React.FC<{ error?: Error; resetError: () => void }> = ({ 
  error, 
  resetError 
}) => {
  const { t } = useTranslation();
  
  const handleRetry = () => {
    // Clear any cached data or state that might be causing issues
    sessionStorage.clear();
    
    resetError();
    
    // Force page refresh if retry doesn't work
    setTimeout(() => {
      if (document.querySelector('[data-error-boundary]')) {
        window.location.reload();
      }
    }, 1000);
  };
  
  return (
    <div className="min-h-[400px] flex items-center justify-center p-4" data-error-boundary>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
            <AlertCircle className="w-6 h-6 text-destructive" />
          </div>
          <CardTitle className="text-foreground">{t('errorBoundary.title')}</CardTitle>
          <CardDescription>
            {t('errorBoundary.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {import.meta.env.DEV && error && (
            <div className="p-3 bg-muted rounded-md">
              <p className="text-sm font-mono text-muted-foreground">{error.message}</p>
              {error.stack && (
                <details className="mt-2">
                  <summary className="text-xs cursor-pointer">Stack trace</summary>
                  <pre className="text-xs mt-1 whitespace-pre-wrap">{error.stack}</pre>
                </details>
              )}
            </div>
          )}
          <Button 
            onClick={handleRetry} 
            className="w-full"
            variant="outline"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('errorBoundary.button')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export const ErrorBoundary = Sentry.withErrorBoundary(ErrorBoundaryClass, {
  fallback: ({ error, resetError }: { error: unknown; resetError: () => void }) => (
    <DefaultErrorFallback 
      error={error instanceof Error ? error : new Error(String(error))} 
      resetError={resetError} 
    />
  ),
  showDialog: false,
});

export default ErrorBoundary;